/* <cs-video>: plays hosted videos (autoplay muted loop while visible, or on a play click) and loads
   YouTube / Vimeo players lazily from the <template> inside. Any [data-video-play] button inside the
   closest [data-video-scope] (or inside the element itself) starts playback. */
class VideoMedia extends HTMLElement {
  connectedCallback() {
    this.kind = this.dataset.kind;
    this.autoplay = this.hasAttribute('data-autoplay');
    this.scope = this.closest('[data-video-scope]') || this;
    this.onPlayClick = (event) => {
      if (!event.target.closest('[data-video-play]')) return;
      event.preventDefault();
      this.play();
    };
    this.scope.addEventListener('click', this.onPlayClick);

    if (this.autoplay && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) this.start(); else this.pause();
      }, { rootMargin: '100px' });
      this.observer.observe(this);
    } else if (this.autoplay) {
      this.start();
    }
  }

  disconnectedCallback() {
    this.scope?.removeEventListener('click', this.onPlayClick);
    this.observer?.disconnect();
  }

  // The video that is on screen (a separate phone video may be hidden).
  get video() {
    const videos = [...this.querySelectorAll('video')];
    return videos.find((v) => v.offsetParent !== null) || videos[0];
  }

  // Starts a muted looping video (or loads the embed) when it comes into view.
  start() {
    if (this.kind === 'hosted') {
      const video = this.video;
      if (!video) return;
      video.muted = true;
      const promise = video.play();
      if (promise && promise.catch) promise.catch(() => {});
    } else {
      this.loadFrame();
    }
  }

  pause() {
    if (this.kind === 'hosted') this.video?.pause();
  }

  // Starts playback after a click on a play button.
  play() {
    if (this.kind === 'hosted') {
      const video = this.video;
      if (!video) return;
      video.controls = true;
      video.muted = false;
      const promise = video.play();
      if (promise && promise.catch) promise.catch(() => {});
    } else {
      this.loadFrame();
    }
    this.classList.add('is-playing');
    this.scope.classList.add('is-video-playing');
    this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
  }

  loadFrame() {
    if (this.querySelector('iframe')) return;
    const template = this.querySelector('template');
    if (!template) return;
    this.append(template.content.cloneNode(true));
    if (this.autoplay) this.classList.add('is-playing');
  }
}
if (!customElements.get('cs-video')) customElements.define('cs-video', VideoMedia);
